import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './CreateCircle.css';

const JoinCircle = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const isLoggedIn = !!localStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid invite link. Please ask for a new invite.');
      setLoading(false);
      return;
    }

    const verify = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/circles/join/verify?token=${token}`
        );
        const data = await res.json();

        if (!res.ok) {
          setError(data.error || 'This invite link is invalid or has expired.');
        } else {
          setInvite(data);
        }
      } catch (err) {
        setError('Failed to verify invite. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    verify();
  }, [token]);

  const handleJoin = async () => {
    if (!isLoggedIn) {
      // Save token to localStorage so we can complete join after login
      localStorage.setItem('pendingCircleInvite', token);
      navigate(`/login?redirect=/circles/join?token=${token}`);
      return;
    }

    setJoining(true);
    setError(null);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/circles/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ token }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Failed to join Circle.');
      } else {
        setSuccess(true);
        // Clear any pending invite
        localStorage.removeItem('pendingCircleInvite');
        // Redirect to circle after short delay
        setTimeout(() => navigate(`/circles/${data.circleId}/manage`), 2000);
      }
    } catch (err) {
      setError('Failed to join. Please try again.');
    } finally {
      setJoining(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="circle-page join-page">
        <div className="join-container">
          <div className="circle-sparkle large">✦</div>
          <p className="join-loading">Verifying your invite...</p>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="circle-page join-page">
        <div className="join-container">
          <div className="join-success-icon">✓</div>
          <h1>Welcome to the Circle!</h1>
          <p>You've successfully joined <strong>{invite?.circleName}</strong>.</p>
          <p className="join-redirect">Taking you there now...</p>
        </div>
      </div>
    );
  }

  // Error state (expired, invalid, etc.)
  if (error && !invite) {
    return (
      <div className="circle-page join-page">
        <div className="join-container">
          <div className="circle-sparkle large">✦</div>
          <h1>Invalid invite</h1>
          <div className="circle-error" style={{ marginTop: '1rem' }}>
            ⚠️ {error}
          </div>
          <p style={{ color: 'var(--text-secondary)', marginTop: '1rem', fontSize: '0.95rem' }}>
            Ask the Circle owner to send a new invite.
          </p>
          <button
            className="create-circle-btn"
            onClick={() => navigate(isLoggedIn ? '/dashboard' : '/login')}
            style={{ marginTop: '1.5rem' }}
          >
            {isLoggedIn ? 'Go to dashboard' : 'Sign in'}
          </button>
        </div>
      </div>
    );
  }

  // Main invite acceptance view
  return (
    <div className="circle-page join-page">
      <div className="join-container">

        {/* Brand mark */}
        <div className="circle-sparkle large">✦</div>
        <p className="join-brand">AirFlyp Circles</p>

        {/* Invite card */}
        <div className="join-card">
          <h1>You're invited to join</h1>
          <div className="join-circle-name">{invite?.circleName}</div>

          <div className="join-details">
            <div className="join-detail-row">
              <span className="join-detail-label">Your role</span>
              <span className="join-detail-value">
                {invite?.role === 'admin' ? 'Admin' : 'Member'}
              </span>
            </div>
            <div className="join-detail-row">
              <span className="join-detail-label">Invite sent to</span>
              <span className="join-detail-value">{invite?.email}</span>
            </div>
            <div className="join-detail-row">
              <span className="join-detail-label">Expires</span>
              <span className="join-detail-value">
                {new Date(invite?.expiresAt).toLocaleDateString('en-US', {
                  month: 'long', day: 'numeric', year: 'numeric'
                })}
              </span>
            </div>
          </div>

          {/* What they'll get access to */}
          <div className="join-perks">
            <p className="join-perks-title">As a member you'll have access to:</p>
            <ul className="join-perks-list">
              <li>✓ Shared client records and session history</li>
              <li>✓ AI transcriptions and summaries</li>
              <li>✓ Team activity feed and notifications</li>
              <li>✓ Collaborative notes on shared clients</li>
            </ul>
          </div>

          {error && (
            <div className="circle-error" style={{ marginBottom: '1rem' }}>
              ⚠️ {error}
            </div>
          )}

          {!isLoggedIn && (
            <p className="join-login-notice">
              You'll need to sign in or create an account to accept this invite.
            </p>
          )}

          <button
            className="create-circle-btn"
            onClick={handleJoin}
            disabled={joining}
          >
            {joining
              ? 'Joining...'
              : isLoggedIn
              ? `Accept invite — join ${invite?.circleName}`
              : 'Sign in to accept invite'}
          </button>

          <p className="billing-note">
            Circles are billed to the workspace owner. No charge to members.
          </p>
        </div>

      </div>
    </div>
  );
};

export default JoinCircle;
